import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { APP_DEPLOY_VERSION } from '../config/appVersion';

import { adminClaimWorkEndpointTemplate } from './adminClaimWorkTelemetry';

// Export Sentry's ErrorBoundary component for use in main.tsx and elsewhere
export const SentryErrorBoundary = Sentry.ErrorBoundary;

interface SentryConfig {
  dsn: string;
  environment: string;
}

class SentryService {
  static initialize(config: SentryConfig): void {
    // Tests deliberately exercise failures; never send their events, traces, or replays.
    if (
      !config.environment ||
      ['dev', 'ci', 'test'].includes(config.environment) ||
      import.meta.env.VITE_E2E_MODE === 'true'
    ) {
      return;
    }

    Sentry.init({
      dsn: config.dsn,
      release: APP_DEPLOY_VERSION,
      environment: config.environment,
      integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
          shouldCreateSpanForRequest: url => !adminClaimWorkEndpointTemplate(url),
        }),
        Sentry.replayIntegration(),
        Sentry.browserProfilingIntegration(),
      ],
      // Set tracing sample rate lower in production
      tracesSampleRate: config.environment === 'production' ? 0.5 : 1.0,
      // Always capture replays when errors occur
      replaysOnErrorSampleRate: 1.0,
      // Set profiling sample rate lower in production... TODO: add necessary Headers to S3 for this to work in production
      profilesSampleRate: config.environment === 'production' ? 0.2 : 1.0,
      beforeBreadcrumb(breadcrumb) {
        // These GET URLs contain search names and selected IDs. Dropping the
        // breadcrumb also prevents Replay's network frame in the installed SDK.
        const requestUrl: unknown = breadcrumb.data?.url;
        if (typeof requestUrl === 'string' && adminClaimWorkEndpointTemplate(requestUrl)) {
          return null;
        }
        // Filter out noisy console logs
        if (
          breadcrumb.category === 'console' &&
          (breadcrumb.message?.includes('chunk') ||
            breadcrumb.message?.includes('[HMR]') ||
            breadcrumb.message?.includes('volume visualization'))
        ) {
          return null;
        }
        return breadcrumb;
      },
      beforeSend(event, hint) {
        // Filter out certain events or sanitize sensitive data
        const error = hint.originalException;

        // Filter out known network errors that don't need to be reported
        if (error && typeof error === 'object' && 'message' in error) {
          const errorMessage = String(error.message);

          // Filter out aborted requests and network connectivity issues
          if (
            errorMessage.includes('The user aborted a request') ||
            errorMessage.includes('Network request failed') ||
            errorMessage.includes('Failed to fetch')
          ) {
            // Don't send these to Sentry in production mode
            if (config.environment === 'production') {
              return null;
            }
            // In debug mode, mark them as low severity
            event.level = 'info';
          }
        }

        return event;
      },
    });
  }

  static logEvent(
    description: string,
    {
      level = 'info', // Default level set to 'info'
      tags = {}, // Default to an empty object
      extra = {}, // Default to an empty object
    }: {
      level?: Sentry.SeverityLevel;
      tags?: Record<string, string>;
      extra?: Record<string, any>;
    } = {}, // Default context object to ensure destructuring works when no second argument is provided
  ) {
    Sentry.captureEvent({
      message: description,
      level,
      tags,
      extra,
    });
  }

  static captureException(
    exception: any,
    {
      level = 'error', // Default level set to 'info'
      tags = {}, // Default to an empty object
      extra = {}, // Default to an empty object
      fingerprint,
    }: {
      level?: Sentry.SeverityLevel;
      tags?: Record<string, string>;
      extra?: Record<string, any>;
      /** Overrides Sentry's stack-trace grouping so related throws land in one issue. */
      fingerprint?: string[];
    } = {}, // Default context object to ensure destructuring works when no second argument is provided
  ) {
    Sentry.captureException(exception, { level, tags, extra, fingerprint });
  }

  static captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
    Sentry.captureMessage(message, level);
  }

  static setUserContext(context: { id: string; email?: string; username?: string }) {
    Sentry.setUser(context);
  }

  static clearUserContext() {
    Sentry.setUser(null);
  }

  static setExtra(key: string, value: any) {
    Sentry.setContext(key, value);
  }

  static setContext(name: string, context: Record<string, any>) {
    Sentry.setContext(name, context);
  }

  static setTag(key: string, value: string) {
    Sentry.setTag(key, value);
  }

  /**
   * Create a span for a specific operation
   */
  static startSpan(
    operation: string,
    description: string,
    callback: (span: Sentry.Span) => unknown,
  ) {
    return Sentry.startSpan(
      {
        op: operation,
        name: description,
      },
      callback,
    );
  }

  /**
   * Create a startInactiveSpan for a specific operation
   */
  static startInactiveSpan(operation: string, description: string): Sentry.Span | undefined {
    return Sentry.startInactiveSpan({
      op: operation,
      name: description,
    });
  }

  /**
   * Trace a function execution with telemetry
   */
  static traceFunction<T>(
    operation: string,
    description: string,
    callback: (span: Sentry.Span) => T,
  ): T {
    return Sentry.startSpan(
      {
        op: operation,
        name: description,
      },
      span => {
        return callback(span);
      },
    );
  }

  /**
   * Trace an async function execution with telemetry
   */
  static async traceFunctionAsync<T>(
    operation: string,
    description: string,
    callback: (span: Sentry.Span) => Promise<T>,
  ): Promise<T> {
    return Sentry.startSpan(
      {
        op: operation,
        name: description,
      },
      async span => {
        return await callback(span);
      },
    );
  }

  static addBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
    Sentry.addBreadcrumb(breadcrumb);
  }

  /**
   * Set an attribute on the current active span or transaction.
   */
  static setTransactionAttribute(key: string, value: string): void {
    const span = Sentry.getActiveSpan();
    if (span) {
      span.setAttribute(key, value);
    }
  }

  /**
   * Set a measurement for performance monitoring
   */
  static setMeasurement(name: string, value: number, unit: string, activeSpan?: Sentry.Span) {
    Sentry.setMeasurement(name, value, unit, activeSpan);
  }
}

export default SentryService;
